import { Button, Input, Select } from "antd";
import { PAGE_SIZE_OPTIONS } from "common/constants";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  bottom: 0;
  width: 98%;
  margin-bottom: 1.5%;
`;
const PageColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledSelect = styled(Select)`
  color: #005754;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 13.714px;
  border: 1px solid #005754;
  margin-left: 12px;
  padding: 0;
  :focus-within {
    outline: none;
  }
  @media screen and (max-width: 1024px) {
    margin-left: 6px;
    font-size: 11px;
  }
`;

const PaginationText = styled.div`
  color: #005754;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 13.714px; /* 114.286% */
  white-space: nowrap;
  padding-right: 4px;
  @media screen and (max-width: 1024px) {
    padding-right: 2px;
    font-size: 10px;
  }
`;
const GotoPageText = styled.div`
  color: #005754;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 13.714px;
  @media screen and (max-width: 1024px) {
    display: none;
  }
`;
const InputContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 87, 84, 0.1);
  background: linear-gradient(180deg, #fff 0%, #f5f5f5 100%);
  margin-left: 10px;
  color: #2e2e2e;
  text-align: center;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 13.714px; /* 114.286% */
  @media screen and (max-width: 1024px) {
    font-size: 11px;
  }
`;
const StyledPageInput = styled(Input)`
  width: 50px;
  border: none;
  // This is for not showing the arrows
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    display: none;
    -webkit-appearance: none;
  }
  /* Optional: Remove arrows on Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;
const StyledButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const PageButtonLink = styled(Button)`
  && {
    padding: 8px;
    color: #005754;
    font-family: Poppins;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 13.714px; /* 114.286% */
    text-decoration-line: underline;
    :focus-within {
      border: 1px solid #005754;
    }
    @media screen and (max-width: 1024px) {
      font-size: 11px;
      padding: 4px;
    }
  }
`;

const PaginationComponent = (props) => {
  const {
    pagination,
    setPagination,
    totalElements,
    totalPages,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    heading
  } = props;
  const { currentPage, pageSize: itemsPerPage } = pagination;
  const [goToPage, setGoToPage] = useState(currentPage + 1);
  const initialPageSize = pageSizeOptions[0];

  const selectPageOptions = useMemo(
    () =>
      pageSizeOptions.map((page) => ({
        value: parseInt(page, 10),
        label: `Show ${page} ${heading || "per page"}`
      })),
    [pageSizeOptions, heading]
  );

  useEffect(() => {
    setGoToPage(currentPage + 1);
  }, [currentPage]);

  const handleItemsPerPageChange = (value) => {
    setPagination({
      currentPage: 0,
      pageSize: value
    });
    setGoToPage(1);
  };

  const handleGoToPageChange = (e) => {
    const parsedValue = parseInt(e.target.value, 10);

    const newPage = Math.min(parsedValue, totalPages);
    setGoToPage(newPage);
  };

  const handleGoToPageBlur = () => {
    if (goToPage === "") {
      setGoToPage(currentPage);
    }
  };

  const handleGoToPage = () => {
    if (goToPage >= 1 && goToPage <= totalPages) {
      setPagination({
        currentPage: goToPage - 1,
        pageSize: itemsPerPage
      });
    }
  };

  const handleGotoPrev = () => {
    setPagination({
      currentPage: currentPage - 1,
      pageSize: itemsPerPage
    });
    setGoToPage(currentPage - 1);
  };
  const handleGotoNext = () => {
    setPagination({
      currentPage: currentPage + 1,
      pageSize: itemsPerPage
    });
    setGoToPage(currentPage + 1);
  };

  const pageRange =
    totalElements > 0
      ? `${currentPage * itemsPerPage + 1} - ${Math.min(
          (currentPage + 1) * itemsPerPage,
          totalElements
        )}
          `
      : 0;

  return (
    <PaginationContainer>
      <PageColumn>
        <PaginationText>
          {t(`showing_${heading || "results"}`)} {pageRange}
        </PaginationText>
        {totalElements > initialPageSize && (
          <StyledSelect
            defaultValue={`Show ${itemsPerPage} ${heading || "per page"}`}
            onChange={handleItemsPerPageChange}
            variant="borderless"
            options={selectPageOptions}
          />
        )}
      </PageColumn>
      <PageColumn>
        <PaginationText>
          {t("page_info", {
            currentPage: totalElements > 0 ? currentPage + 1 : 0,
            totalPages
          })}
        </PaginationText>

        {totalPages > 1 && (
          <>
            <GotoPageText>{` • ${t("go_to_page")}`}</GotoPageText>
            <InputContainer>
              <StyledPageInput
                value={goToPage}
                type="number"
                onPressEnter={handleGoToPage}
                onChange={handleGoToPageChange}
                onBlur={handleGoToPageBlur}
                variant="borderless"
              />
              <StyledButton onClick={handleGoToPage} tabIndex={-1}>
                ⏎
              </StyledButton>
            </InputContainer>
          </>
        )}
      </PageColumn>
      {totalPages > 1 && (
        <PageColumn>
          <PageButtonLink
            type="link"
            onClick={handleGotoPrev}
            disabled={currentPage === 0}
          >
            {t("previous_page")}
          </PageButtonLink>
          <PageButtonLink
            type="link"
            onClick={handleGotoNext}
            disabled={currentPage === totalPages - 1}
          >
            {t("next_page")}
          </PageButtonLink>
        </PageColumn>
      )}
    </PaginationContainer>
  );
};
export default PaginationComponent;
